'use client'
import { useState } from 'react'
import { AnimatePresence, motion } from 'framer-motion'
import { F0Box, F0TagRaw } from '@factorialco/f0-react'
import { DeviceImageShowcase } from './DeviceImageShowcase'
import { EyeVisible } from '@factorialco/f0-react/icons/app'

export interface DeviceImageCarouselProps {
  images: string[]
}

export const DeviceImageCarousel: React.FC<DeviceImageCarouselProps> = ({ images }) => {
  const [active, setActive] = useState(0)
  const [isOpen, setIsOpen] = useState(false)

  if (!images?.length) return null

  const visibleThumbs = images.slice(0, 3)
  const remaining = images.length - 3

  return (
    <>
      <F0Box
        flexDirection="row"
        gap="sm"
        width="full"
      >
        {images.length > 1 && (
          <F0Box
            flexDirection="column"
            gap="sm"
            width="1/4"
          >
            {visibleThumbs.map((src, index) => {
              const isLastVisible = index === 2 && images.length > 3

              if (isLastVisible) {
                return (
                  <MoreThumbnail
                    key={`more-${index}`}
                    src={src}
                    remaining={remaining}
                    onClick={() => {
                      setActive(index)
                      setIsOpen(true)
                    }}
                  />
                )
              }

              return (
                <Thumbnail
                  key={`${src}-${index}`}
                  src={src}
                  isActive={index === active}
                  onClick={() => setActive(index)}
                />
              )
            })}
          </F0Box>
        )}

        <div
          className="cursor-pointer"
          style={{ width: images.length > 1 ? '75%' : '100%' }}
          onClick={() => setIsOpen(true)}
        >
          <F0Box
            flexDirection="row"
            alignItems="center"
            border="default"
            borderRadius="md"
          >
            <AnimatePresence mode="wait">
              <motion.img
                className="w-full object-contain max-h-[300px]"
                style={{ aspectRatio: '16/9' }}
                key={images[active]}
                src={images[active]}
                initial={{ opacity: 0, scale: 0.98 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 1.02 }}
                transition={{ duration: 0.2 }}
              />
            </AnimatePresence>
          </F0Box>
        </div>

        {isOpen && (
          <DeviceImageShowcase
            activeIndex={active}
            images={images}
            onClose={() => setIsOpen(false)}
          />
        )}
      </F0Box>
    </>
  )
}

function Thumbnail({
  src,
  isActive,
  onClick,
}: {
  src: string
  isActive: boolean
  onClick: () => void
}) {
  return (
    <div className="flex-1 cursor-pointer" onClick={onClick}>
      <F0Box
        flexDirection="row"
        justifyContent="center"
        alignItems="center"
        border="default"
        borderRadius="md"
        borderColor={isActive ? 'bold' : undefined}
      >
        <img
          src={src}
          alt=""
          className="p-1 w-full object-contain"
          style={{ aspectRatio: '16/9' }}
        />
      </F0Box>
    </div>
  )
}

function MoreThumbnail({
  src,
  remaining,
  onClick,
}: {
  src: string
  remaining: number
  onClick: () => void
}) {
  return (
    <div className="relative flex-1 cursor-pointer" onClick={onClick}>
      <F0Box
        border="default"
        borderRadius="md"
      >
        <img
          src={src}
          alt=""
          className="p-1 w-full object-contain blur-sm"
          style={{ aspectRatio: '16/9' }}
        />
      </F0Box>
      <div className="absolute inset-0 flex items-center justify-center">
        <F0TagRaw icon={EyeVisible} text={`+${remaining} more`} />
      </div>
    </div>
  )
}
